import {
    Bridge,
    windowSchema,
    Parseable,
    deepLinkSchema,
    GameName,
    bridgeSchema,
} from "inbox-contentful";
import { parseGameParams } from "../validation/game-params";
import { ingameStore } from "inbox-contentful/src/gameCommands";
import { z } from "zod";

const noop = () => {};
export const bridgeShim: Bridge = {
    getParams: <T>(parser: Parseable<T>) =>
        new Promise<T>((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("getParams");
            }
            window["setParams"] = (
                unvalidatedParams: string | Record<string, unknown>,
            ) => {
                const { output, validationError } = parseGameParams<T>(
                    typeof unvalidatedParams !== "string"
                        ? unvalidatedParams
                        : JSON.parse(unvalidatedParams),
                    parser,
                );
                resolve({ gameParams: output, validationError } as T);
            };
            window.sendCommand?.("getParams");
        }),
    refreshJwt: () =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("tokenExpired");
            }
            window.sendCommand?.("tokenExpired");
            resolve(undefined);
        }),
    playSound: (_: string) =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("playSound", _);
            }
            resolve(undefined);
        }),

    // Migrate if needed, but mostly here for the typescript compiler
    show: noop,
    close: noop,
    goBack: () =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("goBack");
            }
            return resolve(history.back());
        }),
    openUrl: (url: string) =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("openUrl?url=" + url);
                return;
            }
            window.location.href = url;
            resolve(undefined);
        }),
    openShop: () =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("openShop");
            }
            const gameName = GameName.parse(window.GAMENAME);
            window.location.href = `${deepLinkSchema[gameName]}://${ingameStore[gameName]}`;
            return resolve(undefined);
        }),
    claimVoucher: () =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("claimVoucher");
            }
            return resolve(undefined);
        }),
    claimVoucherQuiet: () =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("claimVoucherQuiet");
            }
            return resolve(undefined);
        }),
    markTabAsSeen: (tab) =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("markTabAsSeen", tab);
            }
            return resolve(undefined);
        }),
    openUrlWithSso: (urlWithSsoParams) =>
        new Promise((resolve) => {
            if (parent?.DEBUG_LOGGING) {
                window.sendCommand?.("openUrlWithSso");
            }
            const gameName = GameName.parse(window.GAMENAME);
            window.location.href = `${
                deepLinkSchema[gameName]
            }://scidOpenWebSSO?url=${encodeURIComponent(urlWithSsoParams)}`;
            return resolve(undefined);
        }),
    bridgeShim: true,
} as Bridge;

export function makeBridgeMaintenanceModeSafe(
    bridge: z.infer<typeof bridgeSchema>,
): z.infer<typeof bridgeSchema> {
    return {
        ...bridge,
        close: () => {
            if (!bridge.maintenance) {
                return bridge.close();
            }
        },
        claimVoucher: (code: string, ...args: unknown[]) => {
            if (!bridge.maintenance) {
                return bridge.claimVoucher(code, ...args);
            }
        },
        claimVoucherQuiet: (code: string, ...args: unknown[]) => {
            if (!bridge.maintenance) {
                return bridge.claimVoucherQuiet(code, ...args);
            }
        },
        openShop: (...args: unknown[]) => {
            if (!bridge.maintenance) {
                return bridge.openShop(...args);
            }
        },
    };
}

export async function bridge<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TSchema extends z.ZodObject<any> = typeof windowSchema,
    TBridge = z.infer<TSchema>["bridge"],
>(schema?: TSchema): Promise<TBridge> {
    return new Promise<TBridge>((resolve) => {
        const res = (schema ?? windowSchema).safeParse(window);
        if (res.success === false) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).__bridgeResolve = resolve;
            return resolve(bridgeShim as TBridge);
        }

        return resolve(
            makeBridgeMaintenanceModeSafe(res.data.bridge) as TBridge,
        );
    });
}
